import React, { useCallback, useState, useRef } from 'react'
import { Flex, Box, TextArea, Pill } from '@chilipiper/design-system'
import { StyledNormalizedField } from './styles'

const KEY_ENTER = 'Enter'
const KEY_BACKSPACE = 'Backspace'
const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/
const DIVIDER_REGEX = /\s*[,\s|\s|;\s]\s*/

export interface Props {
  guests: string[]
  updateGuests: (guests: string[]) => void
}

export const AdditionalGuestsTextField = ({ guests, updateGuests }: Props) => {
  const [textValue, setTextValue] = useState('')
  const textFieldRef = useRef<HTMLTextAreaElement>(null)
  const updateValueAndGuests = useCallback(
    (value: string) => {
      const splittedValues = value.trim().split(DIVIDER_REGEX)
      const lastValue = splittedValues[splittedValues.length - 1]
      const guestsDraft: string[] = []
      splittedValues.forEach(splittedValue => {
        if (EMAIL_REGEX.test(splittedValue.trim())) {
          guestsDraft.push(splittedValue.trim())
        }
      })
      setTextValue(guestsDraft.includes(lastValue) ? '' : lastValue.trim())
      updateGuests(guests.concat(guestsDraft))
    },
    [setTextValue, updateGuests, guests]
  )
  const onChange = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      event.preventDefault()
      const { value } = event.target
      if (value.endsWith(' ') || value.endsWith(',') || value.endsWith(';')) {
        updateValueAndGuests(value)
      } else {
        setTextValue(value.trim())
      }
    },
    [setTextValue, updateValueAndGuests]
  )
  const onRemove = useCallback(
    (evt: React.MouseEvent<HTMLButtonElement, MouseEvent>, name: string) => {
      evt.stopPropagation()
      evt.preventDefault()
      const filteredGuests = guests.filter(guestItem => guestItem !== name)
      updateGuests(filteredGuests)
    },
    [guests, updateGuests]
  )
  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLElement>) => {
      if (event.key === KEY_ENTER) {
        updateValueAndGuests(textValue)
      } else if (event.key === KEY_BACKSPACE) {
        if (!textValue && guests.length) {
          updateGuests([...guests.slice(0, -1)])
        }
      }
    },
    [updateValueAndGuests, textValue, guests, updateGuests]
  )
  const onFocusTextField = useCallback(
    (evt: React.MouseEvent<HTMLElement, MouseEvent>) => {
      evt.stopPropagation()
      evt.preventDefault()
      textFieldRef.current?.focus()
    },
    [textFieldRef]
  )
  return (
    <Box h={15} pt={1}>
      <TextArea onClick={onFocusTextField} as='div' label='Guests Emails' overflowY='auto'>
        <Flex maxWidth='440px' flexWrap='wrap' overflowY='auto' mt={2}>
          {guests.map(guest => (
            <Box key={guest} w='fit-content' mr={2} mb={2}>
              <Pill
                name={guest}
                data-test-id={`Pill-guest-${guest}`}
                key={guest}
                onRemove={onRemove}
              />
            </Box>
          ))}
          <StyledNormalizedField
            onChange={onChange}
            onKeyDown={onKeyDown}
            data-test-id='AdditionalGuestsTextField-guest'
            onBlur={() => updateValueAndGuests(textValue)}
            value={textValue}
            ref={textFieldRef}
          />
        </Flex>
      </TextArea>
    </Box>
  )
}
